import { resolveUnit } from '../schedule/material-import';
import type { UnitOfMeasure } from '../schedule/unit-of-measure';
import { normalizeName } from '../shared/naming';
import { disciplineNaturalKey, type Discipline } from './discipline';

/**
 * Importação do catálogo de disciplinas.
 *
 * As disciplinas são as mesmas em toda obra — alvenaria é alvenaria na torre A e na
 * reforma do ano que vem —, então o catálogo é **global** e entra uma vez só. Quem já
 * tem a lista de serviços numa planilha traz de lá em vez de digitar cinco, quinze ou
 * quarenta linhas na mão.
 *
 * Tudo aqui é **puro**: recebe as linhas já lidas do arquivo e devolve o que aconteceria.
 * Nada toca o banco antes da confirmação (RN-030).
 *
 * A identidade é o **nome**, não o código: é assim que a chave natural da disciplina já
 * funciona (RN-007), e é o nome que a planilha da obra sempre traz — o código, quase
 * nunca.
 */

export type DisciplineImportField = 'name' | 'code' | 'unit' | 'color';

export interface DisciplineImportColumn {
  readonly field: DisciplineImportField;
  readonly label: string;
  readonly required: boolean;
  /** Cabeçalhos que costumam nomear esta coluna nas planilhas de obra. */
  readonly synonyms: readonly string[];
}

export const DISCIPLINE_IMPORT_COLUMNS: readonly DisciplineImportColumn[] = [
  {
    field: 'name',
    label: 'Disciplina',
    required: true,
    synonyms: [
      'disciplina',
      'disciplinas',
      'nome',
      'nome da disciplina',
      'servico',
      'servicos',
      'etapa',
      'atividade',
      'descricao',
      'especialidade',
    ],
  },
  {
    field: 'code',
    label: 'Código',
    required: false,
    synonyms: ['codigo', 'cod', 'sigla', 'abreviacao', 'referencia', 'ref', 'id'],
  },
  {
    field: 'unit',
    label: 'Unidade',
    required: false,
    synonyms: ['unidade', 'un', 'und', 'unid', 'unidade de medida', 'um', 'medida'],
  },
  {
    field: 'color',
    label: 'Cor',
    required: false,
    synonyms: ['cor', 'color', 'cor na arvore', 'hex'],
  },
];

/** Compara cabeçalhos ignorando acento, caixa e pontuação — "Descrição" casa com "descricao". */
function headerKey(value: string): string {
  return normalizeName(value)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export type DisciplineColumnMapping = Partial<Record<DisciplineImportField, string>>;

/**
 * Sugere o mapeamento a partir dos cabeçalhos.
 *
 * É sugestão, não decisão: a tela mostra o que foi entendido e deixa corrigir antes de
 * qualquer gravação. Uma coluna só serve a um campo — o primeiro que a reivindicar.
 */
export function suggestDisciplineMapping(headers: readonly string[]): DisciplineColumnMapping {
  const mapping: DisciplineColumnMapping = {};
  const taken = new Set<string>();

  for (const column of DISCIPLINE_IMPORT_COLUMNS) {
    const match = headers.find((header) => {
      if (taken.has(header)) return false;
      const key = headerKey(header);
      return key === headerKey(column.label) || column.synonyms.includes(key);
    });
    if (match !== undefined) {
      mapping[column.field] = match;
      taken.add(match);
    }
  }

  return mapping;
}

/**
 * Normaliza a cor escrita na planilha para `#RRGGBB`; `undefined` quando não é cor.
 *
 * Aceita com ou sem `#`, em três ou seis dígitos. Texto que não é cor é simplesmente
 * ignorado: a cor é apoio visual, e recusar a linha por causa dela seria perder a
 * disciplina por um detalhe decorativo.
 */
export function normalizeColor(value: string): string | undefined {
  const raw = value.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return undefined;
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((char) => char + char)
          .join('')
      : raw;
  return `#${full.toLowerCase()}`;
}

export type DisciplineRowStatus = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'INVALID' | 'DUPLICATE';

export interface DisciplineImportIssue {
  readonly field: DisciplineImportField | 'row';
  readonly message: string;
}

export interface DisciplineImportRow {
  /** Número da linha no arquivo, contando o cabeçalho — é o que o usuário enxerga. */
  readonly rowNumber: number;
  readonly status: DisciplineRowStatus;
  readonly name: string;
  readonly code: string | undefined;
  readonly color: string | undefined;
  readonly unitId: string | undefined;
  readonly unitLabel: string;
  /** Disciplina global existente com o mesmo nome, quando houver (RN-007). */
  readonly existingId: string | undefined;
  readonly issues: readonly DisciplineImportIssue[];
}

export interface ClassifyDisciplineInput {
  readonly rows: readonly Record<string, string>[];
  readonly mapping: DisciplineColumnMapping;
  readonly units: readonly UnitOfMeasure[];
  /** Catálogo global atual — é contra ele que cada linha é resolvida. */
  readonly existing: readonly Discipline[];
}

export interface DisciplineImportPlan {
  readonly rows: readonly DisciplineImportRow[];
  readonly summary: {
    readonly total: number;
    readonly create: number;
    readonly update: number;
    readonly unchanged: number;
    readonly invalid: number;
    readonly duplicate: number;
  };
}

/**
 * Classifica cada linha do arquivo.
 *
 * O que decide o destino de uma linha:
 *
 * - sem nome → `INVALID`; é o nome que dá identidade à disciplina;
 * - nome repetido **dentro do arquivo** → `DUPLICATE`, e só a primeira ocorrência vale —
 *   as duas disputariam a mesma chave única;
 * - nome que já existe no catálogo → `UPDATE`, ou `UNCHANGED` se nada mudaria;
 * - o resto → `CREATE`.
 *
 * Unidade que não existe no catálogo **não invalida a linha**: disciplina sem medição é
 * um estado legítimo, e recusar "Elétrica" porque a planilha escreveu "vb" seria trocar
 * um dado ausente por uma linha perdida. O aviso fica na linha, para ser visto antes de
 * confirmar.
 */
export function classifyDisciplineImport(input: ClassifyDisciplineInput): DisciplineImportPlan {
  const existingByKey = new Map(
    input.existing.map((discipline) => [
      disciplineNaturalKey(undefined, discipline.name),
      discipline,
    ]),
  );
  const seenInFile = new Set<string>();
  const rows: DisciplineImportRow[] = [];

  input.rows.forEach((raw, index) => {
    const rowNumber = index + 2; // +1 pelo cabeçalho, +1 porque humano conta do 1
    const issues: DisciplineImportIssue[] = [];

    const pick = (field: DisciplineImportField): string => {
      const column = input.mapping[field];
      return column === undefined ? '' : (raw[column] ?? '').trim();
    };

    const name = pick('name');
    const code = pick('code');
    const unitRaw = pick('unit');
    const colorRaw = pick('color');

    if (name === '') issues.push({ field: 'name', message: 'Nome vazio.' });

    const resolvedUnit = unitRaw === '' ? undefined : resolveUnit(unitRaw, input.units);
    if (unitRaw !== '' && resolvedUnit === undefined) {
      issues.push({
        field: 'unit',
        message: `Unidade "${unitRaw}" não existe no catálogo — a disciplina entra sem medição.`,
      });
    }

    const color = colorRaw === '' ? undefined : normalizeColor(colorRaw);

    const key = disciplineNaturalKey(undefined, name);
    const isDuplicate = name !== '' && seenInFile.has(key);
    if (name !== '') seenInFile.add(key);

    const existing = name === '' ? undefined : existingByKey.get(key);

    let status: DisciplineRowStatus;
    if (name === '') {
      status = 'INVALID';
    } else if (isDuplicate) {
      status = 'DUPLICATE';
      issues.push({ field: 'name', message: 'Disciplina repetida dentro do arquivo.' });
    } else if (existing !== undefined) {
      const changed =
        (existing.code ?? '') !== code ||
        (color !== undefined && (existing.color ?? '') !== color) ||
        false;
      status = changed ? 'UPDATE' : 'UNCHANGED';
    } else {
      status = 'CREATE';
    }

    rows.push({
      rowNumber,
      status,
      name,
      code: code === '' ? undefined : code,
      color,
      unitId: resolvedUnit?.id,
      unitLabel: resolvedUnit?.name ?? (unitRaw === '' ? '—' : unitRaw),
      existingId: existing?.id,
      issues,
    });
  });

  return {
    rows,
    summary: {
      total: rows.length,
      create: rows.filter((row) => row.status === 'CREATE').length,
      update: rows.filter((row) => row.status === 'UPDATE').length,
      unchanged: rows.filter((row) => row.status === 'UNCHANGED').length,
      invalid: rows.filter((row) => row.status === 'INVALID').length,
      duplicate: rows.filter((row) => row.status === 'DUPLICATE').length,
    },
  };
}

export const DISCIPLINE_IMPORT_STATUS_LABEL: Readonly<Record<DisciplineRowStatus, string>> = {
  CREATE: 'Nova',
  UPDATE: 'Atualiza',
  UNCHANGED: 'Sem mudança',
  INVALID: 'Com erro',
  DUPLICATE: 'Repetida',
};
